import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../components/toast/CustomToast'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Pagination from '../../components/common/Pagination'
import MonacoEditor from '../../components/common/MonacoEditor'
import HeadersConfig from '../../components/common/HeadersConfig'
import StepSelector from '../../components/common/StepSelector'
import JSONEditor from '../../components/common/JSONEditor'
import JSONOutput from '../../components/common/JSONOutput'
import Timeline from '../../components/common/Timeline'
import WizardStep from '../../components/common/WizardStep'
import EnvironmentSection from '../../components/common/EnvironmentSection'
import api, { apiCall } from '../../utils/api'
import { eventAPI, supplierAPI, setupAPI } from '../../utils/apiHelpers'

const SimulatorPage = () => {
    const { token } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [jsonInput, setJsonInput] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [timeline, setTimeline] = useState([]);
    const [eventData, setEventData] = useState(null);
    const [allEvents, setAllEvents] = useState([]);
    const [allCompanies, setAllCompanies] = useState([]);
    const [allIntegrations, setAllIntegrations] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedIntegrations, setSelectedIntegrations] = useState([]);
    const [outputData, setOutputData] = useState(null);
    const [isExecutingAll, setIsExecutingAll] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [events, companies, integrations] = await Promise.all([
                    eventAPI.list(1, 100, token),
                    apiCall('GET', '/companies', null, token),
                    apiCall('GET', '/integrations', null, token),
                ]);
                setAllEvents(events.items || []);
                setAllCompanies(companies.items || []);
                setAllIntegrations(integrations.items || []);
            } catch (error) {

            }
        };
        // Only load data once on mount, not when token changes
        if (token) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateJSON = () => {
        setJsonError('');
        setTimeline([]);
        try {
            const parsed = JSON.parse(jsonInput);
            setEventData(parsed);

            const newTimeline = [{
                status: 'success',
                title: 'JSON Validado',
                message: 'O JSON foi parseado com sucesso'
            }];
            setTimeline(newTimeline);
            return parsed;
        } catch (error) {
            const msg = `JSON inválido: ${error.message}`;
            setJsonError(msg);
            setTimeline([{
                status: 'error',
                title: 'Erro ao Validar JSON',
                message: msg
            }]);
            return null;
        }
    };

    const validateEventSchema = () => {
        if (!eventData) return false;
        const newTimeline = [...timeline];

        if (!eventData.event) {
            newTimeline.push({
                status: 'error',
                title: 'Validação de Schema',
                message: 'Campo "event" não encontrado no JSON'
            });
            setTimeline(newTimeline);
            return false;
        }

        const eventExists = allEvents.find(e => e.name === eventData.event);
        if (!eventExists) {
            newTimeline.push({
                status: 'error',
                title: 'Validação de Evento',
                message: `Evento "${eventData.event}" não encontrado no sistema`
            });
            setTimeline(newTimeline);
            return false;
        }

        newTimeline.push({
            status: 'success',
            title: 'Validação de Evento',
            message: `Evento "${eventData.event}" encontrado com sucesso`
        });
        setTimeline(newTimeline);
        return true;
    };

    const validateContentType = () => {
        if (!eventData || !eventData.data) {
            const newTimeline = [...timeline];
            newTimeline.push({
                status: 'error',
                title: 'Validação Content-Type',
                message: 'Campo "data" não encontrado ou é inválido'
            });
            setTimeline(newTimeline);
            return false;
        }

        const newTimeline = [...timeline];
        newTimeline.push({
            status: 'success',
            title: 'Validação Content-Type',
            message: 'Atributo "data" é válido'
        });
        setTimeline(newTimeline);
        return true;
    };

    const handleStepChange = (stepIndex) => {
        if (stepIndex === 0) {
            setCurrentStep(0);
        } else if (stepIndex === 1 && timeline.length > 0) {
            setCurrentStep(1);
        } else if (stepIndex > currentStep) {
            // Can only go forward if current step is valid
            proceedToNextStep();
        } else if (stepIndex < currentStep) {
            setCurrentStep(stepIndex);
        }
    };

    const proceedToNextStep = async () => {
        try {
            if (currentStep === 0) {
                const parsed = validateJSON();
                if (!parsed) {
                    return;
                }
                setCurrentStep(1);
            } else if (currentStep === 1) {
                if (!validateEventSchema()) {
                    return;
                }
                setCurrentStep(2);
            } else if (currentStep === 2) {
                if (!validateContentType()) {
                    return;
                }
                setCurrentStep(3);
            } else if (currentStep === 3) {
                setCurrentStep(4);
            } else if (currentStep === 4) {
                setCurrentStep(5);
            } else if (currentStep === 5) {
                if (selectedIntegrations.length === 0) {
                    toast.warning('Por favor, selecione pelo menos uma integração');
                    return;
                }
                setCurrentStep(6);
            } else if (currentStep === 6) {
                setCurrentStep(7);
            } else if (currentStep === 7) {
                setCurrentStep(8);
            } else if (currentStep === 8) {
                setCurrentStep(9);
            }
        } finally {
        }
    };

    const goBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const executeAllSteps = async () => {
        if (!jsonInput) return;

        setIsExecutingAll(true);
        try {
            // Step 0: Validate JSON
            const parsed = validateJSON();
            if (!parsed) {
                setIsExecutingAll(false);
                return;
            }
            await new Promise(resolve => setTimeout(resolve, 800));
            setCurrentStep(1);
            await new Promise(resolve => setTimeout(resolve, 300));

            // Step 1: Validate Event Schema
            if (!validateEventSchema()) {
                setIsExecutingAll(false);
                return;
            }
            await new Promise(resolve => setTimeout(resolve, 800));
            setCurrentStep(2);
            await new Promise(resolve => setTimeout(resolve, 300));

            // Step 2: Validate Content-Type
            if (!validateContentType()) {
                setIsExecutingAll(false);
                return;
            }
            await new Promise(resolve => setTimeout(resolve, 800));
            setCurrentStep(3);
            await new Promise(resolve => setTimeout(resolve, 300));

            // All validation steps complete
            setCurrentStep(4);
        } finally {
            setIsExecutingAll(false);
        }
    };

    const companyOptions = allCompanies.map(c => ({ id: c.id, label: c.razao_social }));

    // Find integrations associated with the selected company from the company object
    const integrationOptions = selectedCompany
        ? (() => {
            const company = allCompanies.find(c => c.id === selectedCompany);
            return company && company.integrations ? company.integrations : [];
          })()
        : [];

    const steps = [
        'Importar JSON',
        'Validar Schema',
        'Validar Evento',
        'Validar Filtros Globais',
        'Selecionar Empresa',
        'Selecionar Integrações',
        'Validar Filtros',
        'Adaptar Contrato Padrão',
        'Processar Integrações',
        'Resultado'
    ];

    const completedSteps = [];
    const errorSteps = [];

    if (timeline.length > 0) {
        if (timeline[0].status.includes('error')) errorSteps.push(0);
        else completedSteps.push(0);
    }
    if (timeline.length > 1) {
        if (timeline[1].status.includes('error')) errorSteps.push(1);
        else completedSteps.push(1);
    }
    if (timeline.length > 2) {
        if (timeline[2].status.includes('error')) errorSteps.push(2);
        else completedSteps.push(2);
    }
    if (timeline.length > 3) {
        if (timeline[3].status.includes('error')) errorSteps.push(3);
        else completedSteps.push(3);
    }
    if (selectedCompany) completedSteps.push(4);
    if (selectedIntegrations.length > 0) completedSteps.push(5);
    if (currentStep > 6) completedSteps.push(6);
    if (currentStep > 7) completedSteps.push(7);
    if (currentStep > 8) completedSteps.push(8);
    if (currentStep > 9) completedSteps.push(9);

    return (
        <div className="flex-1 flex gap-6 p-8 overflow-hidden">
            {/* Sidebar de Steps */}
            <div className="w-64 flex-shrink-0 overflow-y-auto">
                <StepSelector steps={steps} currentStep={currentStep} onStepChange={handleStepChange} completedSteps={completedSteps} errorSteps={errorSteps} />
            </div>

            {/* Conteúdo Principal */}
            <div className="flex-1 overflow-y-auto">
                {currentStep === 0 && (
                    <WizardStep stepNumber={1} title="Importar JSON" description="Cole ou faça upload de um JSON para simular o evento">
                        <div className="space-y-4">
                            <JSONEditor value={jsonInput} onChange={setJsonInput} error={jsonError} />
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Exemplos:</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <Button
                                        variant="secondary"
                                        onClick={() => setJsonInput(JSON.stringify({
                                            event: allEvents.length > 0 ? allEvents[0].name : 'patient_registered',
                                            data: {
                                                exam: 'Radiografia',
                                                specialty: 'Cardiologia',
                                                patient: 'João Silva',
                                                date: new Date().toISOString()
                                            }
                                        }, null, 2))}
                                        className="text-sm"
                                    >
                                        <span className="material-icons sm">check_circle</span> Exemplo Válido
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => setJsonInput(JSON.stringify({
                                            event: 'evento_inexistente',
                                            data: {
                                                exam: 'ExameInválido',
                                                specialty: 'EspecialidadeInválida'
                                            }
                                        }, null, 2))}
                                        className="text-sm"
                                    >
                                        <span className="material-icons sm">cancel</span> Exemplo Inválido
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </WizardStep>
                )}

                {currentStep === 1 && (
                    <WizardStep stepNumber={2} title="Validação de Schema" description="Validando se o JSON corresponde ao esquema de /event">
                        <Timeline steps={timeline} />
                    </WizardStep>
                )}

                {currentStep === 2 && (
                    <WizardStep stepNumber={3} title="Validação de Content-Type" description="Validando o tipo de conteúdo do atributo data">
                        <Timeline steps={timeline} />
                    </WizardStep>
                )}

                {currentStep === 3 && (
                    <WizardStep stepNumber={4} title="Validação de Filtros Globais" description="Validando exam e specialty contra filtros registrados">
                        <Timeline steps={timeline} />
                    </WizardStep>
                )}

                {currentStep === 4 && (
                    <WizardStep stepNumber={5} title="Selecionar Empresa" description="Escolha a empresa para processamento">
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            {companyOptions.length === 0 ? (
                                <p className="text-sm text-gray-600 dark:text-gray-400 p-4">Nenhuma empresa disponível</p>
                            ) : (
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {companyOptions.map(company => (
                                        <button
                                            key={company.id}
                                            onClick={() => setSelectedCompany(company.id)}
                                            className={`w-full px-4 py-3 transition text-left ${
                                                selectedCompany === company.id
                                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-300'
                                                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                            }`}
                                        >
                                            <p className="font-medium">{company.label}</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </WizardStep>
                )}

                {currentStep === 5 && (
                    <WizardStep stepNumber={6} title="Selecionar Integrações" description="Escolha uma ou mais integrações para receber o evento">
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            {integrationOptions.length === 0 ? (
                                <p className="text-sm text-gray-600 dark:text-gray-400 p-4">Nenhuma integração disponível para esta empresa</p>
                            ) : (
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {integrationOptions.map(integration => (
                                        <label
                                            key={integration.id}
                                            className={`w-full px-4 py-3 transition flex items-start cursor-pointer ${
                                                selectedIntegrations.includes(integration.id)
                                                    ? 'bg-blue-50 dark:bg-blue-900/20'
                                                    : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedIntegrations.includes(integration.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIntegrations([...selectedIntegrations, integration.id]);
                                                    } else {
                                                        setSelectedIntegrations(selectedIntegrations.filter(id => id !== integration.id));
                                                    }
                                                }}
                                                className="mt-1 mr-3 cursor-pointer"
                                            />
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-gray-100">{integration.name}</p>
                                                <p className="text-xs text-gray-600 dark:text-gray-400">{integration.type}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        {selectedIntegrations.length > 0 && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                                {selectedIntegrations.length} integração{selectedIntegrations.length !== 1 ? 's' : ''} selecionada{selectedIntegrations.length !== 1 ? 's' : ''}
                            </p>
                        )}
                    </WizardStep>
                )}

                {currentStep === 6 && (
                    <WizardStep stepNumber={7} title="Validar Filtros" description="Validando se as integrações selecionadas passam pelos filtros">
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Validando filtros para as {selectedIntegrations.length} integração{selectedIntegrations.length !== 1 ? 's' : ''} selecionada{selectedIntegrations.length !== 1 ? 's' : ''}...</p>
                            <Timeline steps={[
                                {
                                    status: 'success',
                                    title: 'Filtro de Exam',
                                    message: 'Todos os filtros de EXAM das integrações selecionadas foram validados com sucesso'
                                },
                                {
                                    status: 'success',
                                    title: 'Filtro de Specialty',
                                    message: 'Todos os filtros de SPECIALTY das integrações selecionadas foram validados com sucesso'
                                }
                            ]} />
                        </div>
                    </WizardStep>
                )}

                {currentStep === 7 && (
                    <WizardStep stepNumber={8} title="Adaptar Contrato Padrão" description="Transformando JSON de entrada em contrato padrão">
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Adaptando evento para contrato padrão do sistema...</p>
                            <Timeline steps={[
                                {
                                    status: 'success',
                                    title: 'Estrutura Normalizada',
                                    message: 'Evento foi normalizado para o contrato padrão'
                                },
                                {
                                    status: 'success',
                                    title: 'Dados Mapeados',
                                    message: 'Todos os campos de dados foram mapeados corretamente'
                                }
                            ]} />
                        </div>
                    </WizardStep>
                )}

                {currentStep === 8 && (
                    <WizardStep stepNumber={9} title="Processar Integrações" description="Transformando contrato padrão e enviando para integrações">
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Processando dados para {selectedIntegrations.length} integração{selectedIntegrations.length !== 1 ? 's' : ''}...</p>
                            <div className="space-y-3">
                                {selectedIntegrations.map(integrationId => {
                                    const integration = allIntegrations.find(i => i.id === integrationId);
                                    return (
                                        <div key={integrationId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="material-icons sm text-green-600">check_circle</span>
                                                <p className="font-medium text-gray-900 dark:text-gray-100">{integration?.name}</p>
                                            </div>
                                            <div className="ml-7 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                                <p>✓ Contrato padrão transformado para {integration?.type || 'integração'}</p>
                                                <p>✓ {integration?.type === 'HL7' ? 'HL7v2' : 'JSON'} gerado com sucesso</p>
                                                <p>✓ Enviado para o serviço</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </WizardStep>
                )}

                {currentStep === 9 && (
                    <WizardStep stepNumber={10} title="Resultado" description="JSON de saída com histórico da simulação">
                        <div className="space-y-4">
                            <JSONOutput data={{
                                input: eventData,
                                output: {
                                    status: 'success',
                                    company: selectedCompany ? allCompanies.find(c => c.id === selectedCompany)?.razao_social : null,
                                    integrations: selectedIntegrations.map(id => {
                                        const integration = allIntegrations.find(i => i.id === id);
                                        return {
                                            name: integration?.name,
                                            type: integration?.type,
                                            status: 'enviado'
                                        };
                                    }).filter(Boolean),
                                },
                                processamento: {
                                    contrato_padrao: {
                                        evento: eventData?.event,
                                        dados: eventData?.data,
                                        timestamp: new Date().toISOString()
                                    },
                                    integracao_transformacoes: selectedIntegrations.map(id => {
                                        const integration = allIntegrations.find(i => i.id === id);
                                        return {
                                            integracao: integration?.name,
                                            tipo: integration?.type,
                                            formato: integration?.type === 'HL7' ? 'HL7v2' : 'JSON',
                                            status: 'enviado'
                                        };
                                    }),
                                    timestamp: new Date().toISOString()
                                },
                                timeline: [
                                    { step: 1, title: 'Evento Recebido', description: `Sistema recebeu evento ${eventData?.event}` },
                                    { step: 2, title: 'Validações Executadas', description: 'Todas as validações (schema, tipo, filtros globais) passaram com sucesso' },
                                    { step: 3, title: 'Filtros Validados', description: `${selectedIntegrations.length} integração${selectedIntegrations.length !== 1 ? 's' : ''} passaram na validação de filtros` },
                                    { step: 4, title: 'Contrato Adaptado', description: 'JSON adaptado para contrato padrão do sistema' },
                                    { step: 5, title: 'Integrações Processadas', description: `Contrato padrão transformado e enviado para ${selectedIntegrations.length} integração${selectedIntegrations.length !== 1 ? 's' : ''}` },
                                    { step: 6, title: 'Fluxo Completo', description: 'Simulação concluída com sucesso' }
                                ]
                            }} />
                        </div>
                    </WizardStep>
                )}

                {/* Navigation Buttons */}
                <div className="mt-8 flex gap-3">
                    <Button
                        onClick={goBack}
                        variant="secondary"
                        disabled={currentStep === 0 || isExecutingAll}
                    >
                        <span className="material-icons sm">arrow_back</span> Voltar
                    </Button>
                    {currentStep < 9 && (
                        <Button
                            onClick={proceedToNextStep}
                            variant="primary"
                           
                            disabled={
                                errorSteps.includes(currentStep) ||
                                (currentStep === 0 && !jsonInput) ||
                                (currentStep === 4 && !selectedCompany) ||
                                (currentStep === 5 && selectedIntegrations.length === 0) || isExecutingAll
                            }
                        >
                            <span className="material-icons sm">arrow_forward</span> Próximo
                        </Button>
                    )}
                    {false && currentStep === 0 && (
                        <Button
                            onClick={executeAllSteps}
                            variant="primary"
                            loading={isExecutingAll}
                            disabled={!jsonInput || isExecutingAll}
                            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                        >
                            <span className="material-icons sm">play_arrow</span> Executar Tudo
                        </Button>
                    )}
                    {currentStep === 9 && (
                        <Button
                            onClick={() => {
                                setCurrentStep(0);
                                setJsonInput('');
                                setTimeline([]);
                                setSelectedCompany(null);
                                setSelectedIntegrations([]);
                            }}
                            variant="primary"
                        >
                            <span className="material-icons sm">refresh</span> Nova Simulação
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SimulatorPage
